import { useCallback, useEffect, useState } from 'react';

import { Button } from '@/components/Button';
import { Dropdown, IOption } from '@/components/Dropdown';
import { PictogramCard } from '@/components/PictogramCard';
import SearchBar from '@/components/SearchBar';
import { Spinner } from '@/components/Spinner';
import useDebounce from '@/hooks/useDebounce';
import { useTitle } from '@/hooks/useTitle';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { requestPictogramsGrouped } from '@/store/modules/pictograms';
import { requestAllTags } from '@/store/modules/tags';
import styles from './styles.module.scss';

const PAGE_SIZE = 5;

export function PictogramsPage() {
  useTitle('DienTEA | Pictogramas');
  const dispatch = useAppDispatch();
  const { listGrouped, loading, total } = useAppSelector((state) => state.pictograms);
  const { all: tags } = useAppSelector((state) => state.tags);

  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 300);

  const [filter, setFilter] = useState<IOption | null>(null);

  const hasMore = total > listGrouped.length;
  const endReached = total === listGrouped.length && listGrouped.length > 0;

  useEffect(() => {
    dispatch(requestAllTags());
  }, [dispatch]);

  useEffect(() => {
    dispatch(
      requestPictogramsGrouped({
        page: 0,
        pageSize: PAGE_SIZE,
        title: debouncedSearch,
        tagId: filter?.key
      })
    );
  }, [debouncedSearch, dispatch, filter]);

  const loadMore = useCallback(async () => {
    if (loading || !hasMore) return;
    await dispatch(
      requestPictogramsGrouped({
        page: listGrouped.length / PAGE_SIZE,
        pageSize: PAGE_SIZE,
        title: debouncedSearch,
        tagId: filter?.key
      })
    );
  }, [listGrouped.length, loading, hasMore, dispatch, debouncedSearch, filter]);

  return (
    <div className={styles.pictogramsPage}>
      <section className={styles.container}>
        <div className={styles.sectionHeader}>
          <SearchBar value={search} onChange={setSearch} />
          <Dropdown
            options={tags ? tags.map((cat) => ({ key: cat.id, label: cat.name })) : []}
            value={filter}
            onChange={setFilter}
            placeholder='Categoría'
          />
        </div>

        {total === 0 && !loading ? <p>No se han encontrado pictogramas.</p> : null}

        <div className={styles.pictograms}>
          {listGrouped.map((group) => (
            <div key={group.tag.id} className={styles.group}>
              <h2 className={styles.groupTitle}>{group.tag.name}</h2>
              <div className={styles.groupContent}>
                {group.pictograms.map((pic) => (
                  <PictogramCard key={pic.id} pictogram={pic} />
                ))}
              </div>
            </div>
          ))}
        </div>

        {!listGrouped.length && debouncedSearch ? (
          <p>No se han encontrado resultados para "{debouncedSearch}".</p>
        ) : null}

        {hasMore ? (
          <>
            {loading && <Spinner />}

            <div className={styles.btnContainer}>
              <Button onClick={loadMore}>{loading ? 'Cargando...' : 'Cargar más'}</Button>
            </div>
          </>
        ) : endReached ? (
          <div className={styles.endMessage}>
            <p>✨ Has visto todos los pictogramas ✨</p>
          </div>
        ) : null}
      </section>
    </div>
  );
}
